import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Switch } from '@headlessui/react';
import {
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/20/solid';
import { TrashIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { nextCopyName } from '../../../utils/duplicateName';
import ModalDelete from '../../modal-delete/modal-delete';
import SlideOver from '../../slide-over/slide-over';
import { useSelector } from 'react-redux';
import optionApi from '../../../services/optionApi';
import storeApi from '../../../services/storeApi';
import { Spin, Select } from 'antd';
import { TWButton, ToggleSwitch } from '../../ui';
import { Tooltip } from 'react-tooltip';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import OptionVisibleModal from './components/option-visible-modal';
import DeleteHidedateModal from './components/delete-hidedate-modal';
import OptionCaloriesField from '../calories/option-calories-field';
import OptionRowMenu from '../option-row-menu';
import { CALORIES_MODE } from '../../../utils/calories';
import PosMappingPickerModal from '../pos-mapping/pos-mapping-picker-modal';
import usePosMappingAccess from '../pos-mapping/use-pos-mapping-access';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function OptionGroupUpsertModal({
  show,
  optionGroupForm,
  setOptionGroupForm,
  onClickOptionGroupFormSubmit,
  getOptionGroupList,
  loading,
  setLoading,
  dialogs,
  setDialogs,
  errors,
  setErrors,
}) {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const { canEdit: canEditMapping } = usePosMappingAccess();
  const [mappingRowIndex, setMappingRowIndex] = useState(null);
  const [deletedItem, setDeletedItem] = useState({ name: '', id: '' });
  const [storeList, setStoreList] = useState([]);
  // Menu'ya bağlı store'un timezone'u — hidedate'i product'taki gibi
  // store saatine göre hesaplamak için (viewer'ın cihaz saatine göre değil).
  const storeTimezoneId = storeList[0]?.timezoneid || null;
  const [selectedOptionVisible, setSelectedOptionVisible] = useState({});
  const [, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const onOptionGroupFormChange = (val, key) => {
    setOptionGroupForm((prev) => ({ ...prev, [key]: val }));
  };

  const onOptionFormChange = (val, key, index) => {
    if (key === 'visible' && !val) {
      onClickOpenVisibleModal(optionGroupForm.options[index], index);
    } else {
      let newOptionList = [...optionGroupForm.options];
      newOptionList[index][key] = val;
      setOptionGroupForm((prev) => ({ ...prev, options: newOptionList }));
    }
  };

  // An option with a future hidedate is "scheduled to hide" — the switch should
  // read OFF (like products), not stay blue just because visible is still true.
  const isScheduledHidden = (option) =>
    !!option.hidedate && new Date() < new Date(option.hidedate);

  // The options exactly as the server last returned them, kept beside the
  // editable draft. `onOptionFormChange` writes uncommitted edits straight into
  // the draft option, so the visibility auto-save below must not spread it.
  const serverOptionsRef = useRef({});

  // Record each option the first time its id is seen — that is the load, before
  // any keystroke can reach it. Later renders carry the *same* option objects
  // (the draft is edited in place), so a recorded row is never overwritten by a
  // dirty one.
  useEffect(() => {
    (optionGroupForm.options || []).forEach((option) => {
      if (option.id && !serverOptionsRef.current[option.id]) {
        serverOptionsRef.current[option.id] = { ...option };
      }
    });
  }, [optionGroupForm.options]);

  useEffect(() => {
    serverOptionsRef.current = {};
  }, [optionGroupForm.id]);

  // Persist a single option's visible/hidedate immediately so the change is not
  // lost when a user forgets to press Save. Only existing options in a saved
  // group can be upserted standalone; new ones still persist on the group save.
  //
  // The upsert replaces the whole row, so every field this control does not own
  // is sent from the stored row rather than the draft. Spreading the draft would
  // let an unsaved calories/price/name edit ride along on a toggle the user
  // never meant as a Save — and Cancel could no longer take it back.
  const persistOptionVisible = async (option) => {
    if (!option.id || !optionGroupForm.id) return;
    const serverOption = serverOptionsRef.current[option.id];
    if (!serverOption) return;
    const body = {
      ...serverOption,
      visible: option.visible,
      hidedate: option.hidedate,
      optiongroupid: optionGroupForm.id,
      name: serverOption.name ? serverOption.name.trim() : serverOption.name,
    };
    try {
      await optionApi.upsert(body);
      serverOptionsRef.current[option.id] = { ...body };
    } catch (e) {
      console.error('Option visibility auto-save failed', e);
    }
  };

  const applyOption = (option, index) => {
    setOptionGroupForm((prev) => ({
      ...prev,
      options: prev.options.map((it, idx) => (idx === index ? option : it)),
    }));
  };

  // Mirrors the product visible behaviour: touching a scheduled switch re-shows
  // the option now; turning OFF opens the hide-duration modal; turning ON shows
  // it. Every path auto-saves.
  const onChangeOptionVisible = (val, index) => {
    const option = optionGroupForm.options[index];
    if (isScheduledHidden(option)) {
      const updated = { ...option, hidedate: '', visible: true };
      applyOption(updated, index);
      persistOptionVisible(updated);
      return;
    }
    if (!val) {
      onClickOpenVisibleModal(option, index);
    } else {
      const updated = { ...option, visible: true, hidedate: '' };
      applyOption(updated, index);
      persistOptionVisible(updated);
    }
  };

  const onClickOpenVisibleModal = (option, index) => {
    setSelectedOptionVisible({ option, index });

    setDialogs((prev) => ({ ...prev, optionVisible: true }));
  };

  const onSelectVisible = async (time) => {
    let { option, index } = selectedOptionVisible;

    option.visible = true;

    // Store timezone kullan. Backend'e timezone offset'li gönder (örn. -08:00),
    // böylece "ne zaman gizlenecek" kararı viewer'ın cihaz saatinden bağımsız olur.
    const tz = storeTimezoneId || 'UTC';
    const base = dayjs().tz(tz);

    if (time === 'tomorrow') {
      option.hidedate = base
        .add(1, 'day')
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ssZ');
    } else if (time === 'forever') {
      option.visible = false;
      option.hidedate = '';
    } else {
      option.hidedate = base
        .add(Number(time), 'hour')
        .format('YYYY-MM-DDTHH:mm:ssZ');
    }

    const newOptionList = optionGroupForm.options.map((item, idx) =>
      idx === index ? option : item,
    );

    setOptionGroupForm((prev) => ({ ...prev, options: newOptionList }));
    await persistOptionVisible(option);
    setDialogs((prev) => ({ ...prev, optionVisible: false }));
    setSelectedOptionVisible({});
  };

  const onClickOpenHidedateModal = (option, val, i) => {
    setSelectedOptionVisible({ option, val, i });

    setDialogs((prev) => ({ ...prev, hidedateDelete: true }));
  };

  const onCloseHidedateModal = () => {
    setDialogs((prev) => ({ ...prev, hidedateDelete: false }));

    setSelectedOptionVisible({});
  };

  const onCloseVisible = () => {
    setDialogs((prev) => ({ ...prev, optionVisible: false }));
  };

  const onSubmitDeleteHideDate = async () => {
    let { option, index } = selectedOptionVisible;

    option.hidedate = null;
    option.visible = true;

    const newOptionList = optionGroupForm.options.map((item, idx) =>
      idx === index ? option : item,
    );

    setOptionGroupForm((prev) => ({ ...prev, options: newOptionList }));
    await persistOptionVisible(option);

    setDialogs((prev) => ({ ...prev, hidedateDelete: false }));

    setSelectedOptionVisible({});
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({
      ...prev,
      name: false,
      price: false,
    }));
    setOptionGroupForm((prev) => ({
      ...prev,
      name: '',
      decription: '',
      displayorder: '',
      storeids: [],
      min: '',
      max: '',
      allowquantities: false,
      companyid: globalCompany.id,
      options: [],
      productoptiongroups: prev.productoptiongroups,
    }));
    setDialogs((prev) => ({ ...prev, optionGroupUpsert: false }));
  };

  const onClickDeleteOptionGroup = (optionGroup) => {
    setDeletedItem(optionGroup);
    setDialogs((prev) => ({ ...prev, optionGroupDelete: true }));
  };

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await optionApi.deleteGroupById(deletedItem.id);
    } catch (error) {
    } finally {
      await getOptionGroupList();
      setDialogs((prev) => ({
        ...prev,
        optionGroupDelete: false,
        optionGroupUpsert: false,
      }));
      setLoading((prev) => ({
        ...prev,
        deleteLoading: false,
        upsertLoading: false,
      }));
    }
  };

  const onClickDeleteOption = async (option) => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      if (option.id) {
        await optionApi.deleteById(option.id);
      }
      const newOptionList = optionGroupForm.options.filter(
        (item) => item !== option,
      );

      setOptionGroupForm((prev) => ({ ...prev, options: newOptionList }));
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const onClickAddOption = () => {
    setOptionGroupForm((prev) => ({
      ...prev,
      options: [
        ...prev.options,
        {
          name: '',
          displayorder:
            prev.options.length > 0
              ? Number(prev.options[prev.options.length - 1].displayorder) + 1
              : 1,
          price: 0,
          visible: true,
          optiongroupid: optionGroupForm.id,
          hidedate: '',
          calories: '',
          caloriesmode: CALORIES_MODE.ADD,
        },
      ],
    }));
  };

  const onDuplicateOption = (i) => {
    const src = optionGroupForm.options[i];
    const siblingNames = optionGroupForm.options.map((o) => o.name);
    const copy = {
      ...src,
      id: undefined,
      name: nextCopyName(src.name, siblingNames),
    };
    setOptionGroupForm((prev) => ({
      ...prev,
      options: [
        ...prev.options.slice(0, i + 1),
        copy,
        ...prev.options.slice(i + 1),
      ],
    }));
  };

  const getOptionList = async () => {
    try {
      setLoading((prev) => ({ ...prev, optionsLoading: true }));

      const list = await optionApi.list(optionGroupForm.id);
      if (list.data.data.length > 0) {
        setOptionGroupForm((prev) => ({
          ...prev,
          options: list.data.data,
        }));
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, optionsLoading: false }));
    }
  };

  const handleSwap = async (currentIndex, targetIndex) => {
    if (targetIndex < 0 || targetIndex >= optionGroupForm.options?.length)
      return;

    setLoading((prev) => ({ ...prev, categoryLoading: true }));

    setOptionGroupForm((prevOptionsGroups) => {
      let newOptions = [...prevOptionsGroups.options];

      const tempDisplayOrder = newOptions[currentIndex].displayorder;

      newOptions[currentIndex].displayorder =
        newOptions[targetIndex].displayorder;
      newOptions[targetIndex].displayorder = tempDisplayOrder;

      const tempItem = newOptions[currentIndex];
      newOptions[currentIndex] = newOptions[targetIndex];
      newOptions[targetIndex] = tempItem;

      return { ...prevOptionsGroups, options: newOptions };
    });
  };

  const getStoreList = useCallback(async () => {
    setLoading((prev) => ({ ...prev, storeLoading: true }));

    try {
      const query = `companyid=${globalCompany.id}`;
      const list = await storeApi.list(query);
      if (list.data.data.length > 0) {
        setStoreList(list.data.data);
      }
    } catch (error) {}
    setLoading((prev) => ({ ...prev, storeLoading: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getStoreList();
  }, [getStoreList]);

  useEffect(() => {
    if (optionGroupForm.id) {
      getOptionList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionGroupForm.id]);

  return (
    <div>
      <SlideOver
        show={show}
        onClose={() => onCloseUpsert()}
        title={
          optionGroupForm.id ? 'Edit Modifier Group' : 'Create Modifier Group'
        }
        subtitle="Set group name, limits and options for this modifier."
        maxWidth="max-w-2xl"
        footer={
          <div className="grid grid-cols-12 gap-3">
            {optionGroupForm.id && (
              <TWButton
                variant="danger"
                icon={TrashIcon}
                disabled={loading.deleteLoading}
                loading={loading.deleteLoading}
                onClick={() => onClickDeleteOptionGroup(optionGroupForm)}
                className="col-span-2 w-full"
              />
            )}
            <TWButton
              variant="default"
              label="Cancel"
              disabled={loading.upsertLoading}
              onClick={() => onCloseUpsert()}
              className={
                optionGroupForm.id ? 'col-span-5 w-full' : 'col-span-6 w-full'
              }
            />
            <TWButton
              variant="primary"
              label={optionGroupForm.id ? 'Save' : 'Create'}
              disabled={loading.upsertLoading}
              loading={loading.upsertLoading}
              onClick={() => onClickOptionGroupFormSubmit()}
              className={
                optionGroupForm.id ? 'col-span-5 w-full' : 'col-span-6 w-full'
              }
            />
          </div>
        }
      >
        <div className="space-y-5 dark:text-slate-200">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Modifier Group Name
            </label>
            <div className="my-2">
              <input
                value={optionGroupForm.name}
                onChange={(e) => {
                  onOptionGroupFormChange(e.target.value, 'name');
                }}
                maxLength={60}
                type="text"
                name="name"
                id="name"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="Option Name"
              />
              <div className="h-2 mt-1  text-xs">
                {errors.name && (
                  <p className="text-red-700">
                    Modifier Group name can not be empty
                  </p>
                )}
              </div>
            </div>
          </div>
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Modifier Group Description (Optional)
            </label>
            <div className="my-2">
              <input
                value={optionGroupForm.description}
                onChange={(e) => {
                  onOptionGroupFormChange(e.target.value, 'description');
                }}
                maxLength={400}
                type="text"
                name="description"
                id="description"
                className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                placeholder="Modifier Group Description"
              />
              <div className="h-2 mt-1  text-xs"></div>
            </div>
          </div>
          <div>
            <label
              htmlFor="stores"
              className="block text-sm font-medium text-gray-700 dark:text-slate-400"
            >
              Stores
            </label>
            {!loading.storeLoading && (
              <div className="mt-1">
                <Select
                  mode="multiple"
                  allowClear
                  size="large"
                  style={{
                    width: '100%',
                  }}
                  placeholder="Stores"
                  value={optionGroupForm?.storeids}
                  onChange={(val) => onOptionGroupFormChange(val, 'storeids')}
                  options={storeList.map((x) => ({
                    label: x.name,
                    value: x.id,
                  }))}
                />
                <div className="h-2 mt-1  text-xs">
                  {errors.stores && (
                    <p className="text-red-700">Stores can not be empty</p>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="justify-between pb-2 grid grid-cols-2 border-b-2 gap-x-5 dark:border-slate-500">
            <div className="col-span-2 sm:col-span-1">
              <label
                htmlFor="min"
                className="block text-sm font-medium text-gray-700 dark:text-slate-400"
              >
                Min Select
              </label>
              <div className="my-2">
                <input
                  value={optionGroupForm.min}
                  onChange={(e) =>
                    onOptionGroupFormChange(e.target.value, 'min')
                  }
                  type="number"
                  name="min"
                  id="min"
                  className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                  placeholder="Min Option"
                />
                <div className="h-2 my-2  text-xs">
                  {errors.min && (
                    <p className="text-red-700">Min Select can not be empty</p>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label
                htmlFor="max"
                className="block text-sm font-medium text-gray-700 dark:text-slate-400"
              >
                Max Select
              </label>
              <div className="my-2">
                <input
                  value={optionGroupForm.max}
                  onChange={(e) =>
                    onOptionGroupFormChange(e.target.value, 'max')
                  }
                  type="number"
                  name="max"
                  id="max"
                  className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                  placeholder="Max Option"
                />
                <div className="h-2 mt-1  text-xs">
                  {errors.max && (
                    <p className="text-red-700">Max Select can not be empty</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-start justify-between gap-4 border-b-2 pb-4 dark:border-slate-500">
            <div>
              <span className="block text-sm font-medium text-gray-700 dark:text-slate-400">
                Allow multiple of the same option
              </span>
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                Customers can pick one option more than once — each pick counts
                toward Max Select
              </p>
            </div>
            <ToggleSwitch
              checked={!!optionGroupForm.allowquantities}
              onChange={(val) => onOptionGroupFormChange(val, 'allowquantities')}
              ariaLabel="Allow multiple of the same option"
              className="mt-1"
            />
          </div>
          {loading.optionsLoading ? (
            <div className="w-full flex justify-center py-4">
              <Spin />
            </div>
          ) : (
            <>
              {optionGroupForm.options.length > 0 && (
                <div className="pt-2">
                  {optionGroupForm.options.map((option, i) => {
                    return (
                      <div
                        key={i}
                        className="border-b border-gray-200 pb-3 last:border-0 last:pb-0 dark:border-slate-700 sm:border-0 sm:pb-2 sm:last:pb-2"
                      >
                        <div className="flex items-start justify-start gap-x-2">
                          <div className="flex shrink-0 flex-col self-center pr-1">
                            <button
                              type="button"
                              onClick={() => handleSwap(i, i - 1)}
                              disabled={i === 0}
                              className={classNames(
                                i === 0 ? 'invisible' : 'block',
                                'text-gray-500 hover:text-gray-800 dark:text-sky-400 dark:hover:text-sky-900',
                              )}
                            >
                              <ChevronUpIcon className="h-5 w-5 text-blue-400 dark:text-slate-200" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSwap(i, i + 1)}
                              disabled={i === optionGroupForm.options?.length - 1}
                              className={classNames(
                                i === optionGroupForm.options?.length - 1
                                  ? 'invisible'
                                  : 'block',
                                'text-gray-500 hover:text-gray-800 dark:text-sky-400 dark:hover:text-sky-900',
                              )}
                            >
                              <ChevronDownIcon className="h-5 w-5 text-blue-400 dark:text-slate-200" />
                            </button>
                          </div>
                          <div className="flex min-w-0 flex-1 flex-wrap items-start gap-x-2 gap-y-2 sm:flex-nowrap">
                            <div className="min-w-0 grow basis-full sm:basis-0">
                              <label
                                htmlFor="name"
                                className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                              >
                                Option Name
                              </label>
                              <div className="mt-1 col-span-1">
                                <input
                                  value={option.name}
                                  onChange={(e) => {
                                    onOptionFormChange(e.target.value, 'name', i);
                                  }}
                                  type="text"
                                  name="name"
                                  id="name"
                                  className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                                  placeholder="Option Name"
                                />
                                <div className="h-2 mt-1  text-xs">
                                  {errors.optionname && (
                                    <p className="text-red-700">
                                      Option Name can not be empty
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="w-28 shrink-0">
                              <label
                                htmlFor="price"
                                className="block text-sm font-medium text-gray-900 dark:text-slate-400"
                              >
                                Price
                              </label>
                              <div className="relative mt-1 rounded-md shadow-sm">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                  <span className="text-gray-500 sm:text-sm dark:text-slate-200">
                                    $
                                  </span>
                                </div>
                                <input
                                  value={option.price}
                                  onChange={(e) =>
                                    onOptionFormChange(e.target.value, 'price', i)
                                  }
                                  type="number"
                                  name="price"
                                  id="price"
                                  className="block w-full rounded-md border-0 py-1.5 pl-7 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 dark:ring-slate-500 placeholder-black placeholder-opacity-25  focus:border-blue-500 focus:outline-none focus:ring-blue-500  dark:focus:border-sky-400 dark:focus:ring-sky-400 sm:text-sm sm:leading-6 dark:bg-slate-800 dark:placeholder:text-white dark:text-slate-200"
                                  placeholder="0.00"
                                  aria-describedby="price-currency"
                                />
                              </div>
                              <div className="h-2 mt-1  text-xs"></div>
                            </div>
                            <div className="order-last w-full pt-0 sm:order-none sm:w-40 sm:pt-6">
                              {option.squareid ? (
                                <p className="truncate text-xs text-gray-500 dark:text-slate-400">
                                  Square: {option.squarename || option.squareid}
                                  {option.squaremissing && (
                                    <span className="ml-1 font-semibold text-rose-600">
                                      (missing)
                                    </span>
                                  )}
                                </p>
                              ) : (
                                canEditMapping && (
                                  <button
                                    type="button"
                                    onClick={() => setMappingRowIndex(i)}
                                    className="text-xs text-sky-600 underline dark:text-sky-300"
                                  >
                                    Map to Square
                                  </button>
                                )
                              )}
                              {option.squareid && canEditMapping && (
                                <button
                                  type="button"
                                  onClick={() => setMappingRowIndex(i)}
                                  className="text-xs text-sky-600 underline dark:text-sky-300"
                                >
                                  Change
                                </button>
                              )}
                            </div>
                            {/* Option calories hidden 2026-07-27 (main-option-only
                                scope-down). Data still round-trips via {...item} on
                                save. Re-enable: restore <OptionCaloriesField/> here;
                                component in components/menu/calories/. */}
                            {option.hidedate &&
                              new Date() < new Date(option.hidedate) && (
                                <>
                                  <Tooltip id="hidedate" />
                                  <div className="flex mt-4 shrink-0">
                                    <CalendarDaysIcon
                                      onClick={() =>
                                        onClickOpenHidedateModal(option, true, i)
                                      }
                                      data-tooltip-id="hidedate"
                                      data-tooltip-content={`This option will be hidden on ${dayjs(
                                        option.hidedate,
                                        'YYYY-MM-DD hh:mm A',
                                      ).format('MM/DD/YYYY hh:mm A')}`}
                                      className={classNames(
                                        'text-slate-500 z-10',
                                        'w-5 h-5 hover:text-gray-800 cursor-pointer dark:text-sky-400 dark:hover:text-sky-900',
                                      )}
                                    />
                                  </div>
                                </>
                              )}
                            <div className="shrink-0 px-2">
                              <label
                                htmlFor="visible"
                                className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                              >
                                Visible
                              </label>
                              <div className="mt-1">
                                <Switch
                                  checked={option.visible && !isScheduledHidden(option)}
                                  onChange={(e) => onChangeOptionVisible(e, i)}
                                  className={classNames(
                                    option?.visible && !isScheduledHidden(option)
                                      ? 'bg-blue-600'
                                      : 'bg-gray-200',
                                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                                  )}
                                >
                                  <span
                                    aria-hidden="true"
                                    className={classNames(
                                      option?.visible && !isScheduledHidden(option)
                                        ? 'translate-x-5'
                                        : 'translate-x-0',
                                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                    )}
                                  />
                                </Switch>
                              </div>
                            </div>
                            <div className="shrink-0 self-start pt-6">
                              <OptionRowMenu
                                onDuplicate={() => onDuplicateOption(i)}
                                onDelete={() => onClickDeleteOption(option)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          <div>
            <div className="pt-2 justify-center dark:hover:bg-slate-700 flex p-1 text-sm font-medium rounded-md text-blue-500 dark:text-slate-200">
              <div
                className="flex items-center cursor-pointer justify-center gap-1 hover:bg-slate-50"
                onClick={() => onClickAddOption()}
              >
                <button
                  type="button"
                  className="rounded-full bg-blue-600 p-1 text-white shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                >
                  <PlusIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                <span className="truncate font-semibold text-[0.925rem]">
                  Add Option
                </span>
              </div>
            </div>
          </div>
          <div className="h-2 mt-1  text-xs"></div>
        </div>
      </SlideOver>
      <PosMappingPickerModal
        open={mappingRowIndex !== null}
        level="option"
        onClose={() => setMappingRowIndex(null)}
        onSelect={(id, name) => {
          onOptionFormChange(id, 'squareid', mappingRowIndex);
          onOptionFormChange(name, 'squarename', mappingRowIndex);
        }}
      />
      <OptionVisibleModal
        onSelectVisible={onSelectVisible}
        show={dialogs.optionVisible}
        loading={loading.visibleLoading}
        errors={errors}
        onCloseVisible={onCloseVisible}
      />
      <DeleteHidedateModal
        show={dialogs.hidedateDelete}
        loading={loading.upsertLoading}
        onClose={onCloseHidedateModal}
        onSubmit={onSubmitDeleteHideDate}
      />
      <ModalDelete
        title="Modifier Group"
        text={`Are you sure you want to delete "${deletedItem.name}"? All associated data will be permanently removed.`}
        loading={loading.deleteLoading}
        open={dialogs.optionGroupDelete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, optionGroupDelete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedItem}
      />
    </div>
  );
}
